import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import EditableText from "@/components/editor/EditableText";
import { cdn, srcSet } from "@/lib/img";

const IMG_FOG = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/xkvkomv6_IMG_7542%20%281%29.jpeg";
const IMG_SHORE = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0zj0zjrb_IMG_7577.jpeg";
const IMG_EXCAVATOR = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/1iyby5ze_IMG_8792.jpeg";
const IMG_ROAD = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/n4fwk8zv_IMG_8775.jpeg";
const IMG_LAND = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/k3m5syys_IMG_8576.JPEG";
const IMG_DAYDREAM = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/eafrlk1w_IMG_8731.jpeg";
const IMG_CANADA_DAY = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/vpnk7ari_IMG_8803.jpeg";
const IMG_MAKING_REAL = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/kzbxkx3n_IMG_8994.jpeg";
const IMG_CANADA_DAY_2 = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/5r1u4m94_IMG_6060%20%281%29.jpeg";

const CHAPTERS = [
  {
    n: "01",
    date: "Spring 2025",
    title: "The Retirement Daydream",
    image: { src: IMG_DAYDREAM, alt: "Isobel watching the Bay of Fundy through binoculars from the rocky shore", side: "right" },
    paragraphs: [
      "Somewhere around our fifty-fifth year, Isobel and I started having the conversation a lot of people our age quietly begin having: what does the next chapter actually look like?",
      "For us, it kept coming back to the same image — hitching up a trailer and slowly making our way across Canada. No rush, no itinerary, just open road and whatever's around the next bend. We love that idea. We still do.",
      "But there was one problem with the provincial park version of that dream: privacy. Campgrounds are wonderful, but they're crowded. You're six feet from your neighbours and you can hear everything. We started wondering — what if we owned a small piece of land somewhere beautiful? Somewhere we could just pull in, unhook, and feel like the place was ours?",
    ],
    closer: "That thought, small as it was, is where Whale Cove begins.",
  },
  {
    n: "02",
    date: "Canada Day weekend – July 2025",
    title: "The first trip out.",
    image: { src: IMG_CANADA_DAY, alt: "The Bay of Fundy from the rocky shore, a fishing boat on the horizon", side: "right" },
    secondaryImage: {
      src: IMG_CANADA_DAY_2,
      alt: "The overgrown trail winding through the forest down to the Bay of Fundy — the path we walked the morning we decided",
      caption: "The trail winding through the forest down to the sea — the path we walked the morning we decided.",
    },
    paragraphs: [
      "Nova Scotia kept coming up. Property prices that made us look twice. Coastline that looked unreal in photos. So we flew out for the long weekend to go have a look.",
      "On the last day of July, we walked the first two properties on foot. Both were a bust. On one we couldn't get a hundred feet through the brush — with a kilometre still to go before the waterfront. The second was just as bad. We drove back to the inn a little disheartened.",
      "That evening, scrolling through listings, a new one appeared that had just posted that day: fifteen acres — a twelve-acre parcel and a three-acre parcel sold together — with a combined five hundred and fifty feet of Bay of Fundy waterfront, an old shared access road, and a trail winding through the forest down to the sea. We looked at it the next morning and felt something click.",
      "On the way back up to where we'd parked, we ran into a man clearing a trail on the neighbouring lot. That was George. It turned out he was the seller. We stood there talking for a long time — he told us the whole history of the property, the land, the Neck. That conversation sealed it.",
    ],
    pullQuote: "We put in an offer. It was accepted that same day.",
    pullQuoteAttribution: "We love the fact that we bought this great piece of Canada on Canada Day.",
    closer: "This wasn't just a parking spot for a trailer anymore. This was something bigger, even if we didn't yet know how big.",
  },
  {
    n: "03",
    date: "Fall of 2025",
    title: "The Road Changes Everything",
    image: { src: IMG_EXCAVATOR, alt: "Excavator cutting the access road through the Nova Scotia forest", side: "left" },
    paragraphs: [
      "That fall, I flew out with my brother to survey the land in person and figure out how we were going to get in — where the road would cut, where the parking would sit, where the sites would go. You can only plan so much from satellite photos. The rest you have to walk.",
      [
        "That same trip, we started working with David Haynes of ",
        { text: "H.D. Construction & Development", href: "https://gohd.ca/" },
        ". Dave knew the Neck, knew the rock, and wasn't fazed by what we were asking. He was the right person to make the old shared access road accessible again.",
      ],
      "George's parcel — the original one — sat further back from the road, and getting to it meant building our own access in. We started planning. That's when reality hit.",
      "Whale Cove is solid rock. It doesn't grade. It doesn't drain. Getting a road to that original property was going to require hauling in an enormous amount of fill, and the numbers climbed fast. We got about halfway there before we stopped and looked at each other.",
      "There was another property — closer to the road, smaller, but with its own stretch of stunning Bay of Fundy waterfront. The math was suddenly obvious: buy that one, stop the road there, get it operational, and let it generate the income to eventually develop the original piece.",
    ],
    pullQuote: "We bought 3.5 acres with 175 feet of waterfront — and that was the start of Site One.",
  },
  {
    n: "04",
    date: "Fall – December 2025",
    title: "Into December",
    image: { src: IMG_LAND, alt: "On the way to Site A", side: "right" },
    paragraphs: [
      "The excavation crew kept working through the fall, cutting and filling the shared access road that would serve both properties. By December, they'd gotten to the point where the road just touches the edge of our land — a real milestone, even if it didn't look like much yet.",
      "I made a solo trip out in December to make the call on where exactly the driveway would branch onto our property. I'd hired a drone photographer to capture the lay of the land, thinking the aerial shots would help me decide from home — but some decisions you just have to make standing in the mud.",
    ],
    closer: "Experience has taught me that if you're not on-site when these calls get made, the result rarely matches what you had in your head. So I went.",
  },
  {
    n: "05",
    date: "April 2026",
    title: "Making it Real",
    image: { src: IMG_MAKING_REAL, alt: "The freshly built stone-and-gravel access road cutting through the birch and spruce forest", side: "left" },
    paragraphs: [
      "This past April, Isobel and I came out together for the first time since the purchase. We walked every inch of it — cleared trails down to the water, figured out where the trailer pads will go, stood on the shoreline and let ourselves properly dream about what this place is becoming.",
      "The contractor brought in more fill to push the road across the last stretch. I ran the chainsaw and knocked down the trees that needed to come out, and before long we had the whole area cleared and reasonably level. The plan now is a base layer of four-inch stone, topped with A gravel — a clean, solid surface that'll serve guests for years.",
    ],
    closer: "The Bay of Fundy does the rest.",
  },
];

/* ─── Chapter Block ─── */
function Chapter({ chapter, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });
  const hasImage = !!chapter.image;
  const imgRight = hasImage && chapter.image.side === "right";
  const imgLeft = hasImage && chapter.image.side === "left";

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="relative py-10 sm:py-14 lg:py-16 first:pt-6 last:pb-4"
      data-testid={`story-chapter-${chapter.n}`}
    >
      {/* Chapter header — huge numeral + label */}
      <header className="mb-8 sm:mb-10 max-w-3xl">
        <div className="flex items-baseline gap-5 sm:gap-8 mb-5">
          <span className="font-display text-wc-teal/25 text-7xl sm:text-8xl lg:text-9xl font-bold leading-none tracking-tight select-none"
            aria-hidden="true">
            {chapter.n}
          </span>
          <div className="pb-2 sm:pb-4">
            <p className="font-body text-wc-slate/70 text-[11px] sm:text-xs uppercase tracking-[0.25em] mb-1">
              Chapter {parseInt(chapter.n, 10)}
            </p>
            <p className="font-accent italic text-wc-teal text-sm sm:text-base">
              {chapter.date}
            </p>
          </div>
        </div>
        <h3 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
          {chapter.title}
        </h3>
      </header>

      {/* Body */}
      <div className={`grid grid-cols-1 ${hasImage ? "lg:grid-cols-12" : ""} gap-10 lg:gap-14 items-start`}>
        {/* Image left */}
        {imgLeft && (
          <motion.figure
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="rounded-sm overflow-hidden aspect-[4/5] shadow-sm">
              <img src={cdn(chapter.image.src, 900)} srcSet={srcSet(chapter.image.src)} sizes="(max-width: 1024px) 100vw, 40vw"
                alt={chapter.image.alt}
                loading="lazy" decoding="async"
                className="w-full h-full object-cover wc-img grayscale-[0.1] hover:grayscale-0 transition-all duration-700" />
            </div>
          </motion.figure>
        )}

        {/* Prose */}
        <div className={`${hasImage ? "lg:col-span-7" : ""} max-w-2xl ${imgLeft ? "" : "lg:ml-0"}`}>
          <div className="space-y-5 sm:space-y-6 font-body text-wc-forest/85 text-base sm:text-lg leading-[1.75]">
            {chapter.paragraphs.map((p, i) => {
              const content = Array.isArray(p) ? p.map((part, j) =>
                typeof part === "string"
                  ? part
                  : (
                    <a key={j} href={part.href} target="_blank" rel="noopener noreferrer"
                      className="text-wc-teal underline decoration-wc-teal/30 underline-offset-4 hover:decoration-wc-teal transition-colors"
                      data-testid={`story-link-${part.href.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      {part.text}
                    </a>
                  )
              ) : p;
              return (
                <p key={i}
                  className={i === 0 ? "first-letter:font-display first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:leading-[0.9] first-letter:text-wc-forest" : ""}>
                  {content}
                </p>
              );
            })}
          </div>

          {chapter.pullQuote && (
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="my-8 sm:my-10 pl-6 border-l-2 border-wc-teal"
            >
              <p className="font-display italic text-wc-forest text-2xl sm:text-3xl lg:text-[2rem] leading-[1.3] font-medium">
                "{chapter.pullQuote}"
              </p>
              {chapter.pullQuoteAttribution && (
                <footer className="font-accent italic text-wc-teal text-base sm:text-lg mt-3">
                  — {chapter.pullQuoteAttribution}
                </footer>
              )}
            </motion.blockquote>
          )}

          {chapter.closer && (
            <p className="mt-8 font-body italic text-wc-forest text-lg sm:text-xl leading-[1.6] max-w-xl">
              {chapter.closer}
            </p>
          )}
        </div>

        {/* Image right */}
        {imgRight && (
          <motion.figure
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 lg:col-start-8 lg:sticky lg:top-28 lg:-mt-2"
          >
            <div className="rounded-sm overflow-hidden aspect-[4/5] shadow-sm">
              <img src={cdn(chapter.image.src, 900)} srcSet={srcSet(chapter.image.src)} sizes="(max-width: 1024px) 100vw, 40vw"
                alt={chapter.image.alt}
                loading="lazy" decoding="async"
                className="w-full h-full object-cover wc-img grayscale-[0.1] hover:grayscale-0 transition-all duration-700" />
            </div>
          </motion.figure>
        )}
      </div>

      {/* Wide secondary image — editorial "second beat" within a chapter */}
      {chapter.secondaryImage && (
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-12 sm:mt-16"
          data-testid={`story-chapter-${chapter.n}-secondary`}
        >
          <div className="rounded-sm overflow-hidden aspect-[16/9] shadow-sm">
            <img
              src={cdn(chapter.secondaryImage.src, 1200)}
              srcSet={srcSet(chapter.secondaryImage.src)}
              sizes="(max-width: 1024px) 100vw, 90vw"
              alt={chapter.secondaryImage.alt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover wc-img grayscale-[0.08] hover:grayscale-0 transition-all duration-700"
            />
          </div>
          {chapter.secondaryImage.caption && (
            <figcaption className="mt-4 font-accent italic text-wc-teal text-sm sm:text-base leading-relaxed max-w-2xl">
              {chapter.secondaryImage.caption}
            </figcaption>
          )}
        </motion.figure>
      )}

      {/* Divider ornament between chapters */}
      {index < CHAPTERS.length - 1 && (
        <div className="flex items-center justify-center gap-4 mt-10 sm:mt-14" aria-hidden="true">
          <span className="h-px w-10 sm:w-16 bg-wc-teal/30" />
          <span className="font-accent italic text-wc-teal/50 text-sm">&sect;</span>
          <span className="h-px w-10 sm:w-16 bg-wc-teal/30" />
        </div>
      )}
    </motion.article>
  );
}

/* ─── Hero intro ─── */
function StoryIntro() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.header
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9 }}
      className="wc-section bg-wc-cream pb-8 sm:pb-12"
    >
      <div className="max-w-4xl mx-auto text-center">
        <p className="font-accent italic text-wc-teal text-base sm:text-lg mb-6">An Origin Story</p>
        <EditableText contentKey="story_heading" as="h2"
          className="font-display text-wc-forest text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6"
          defaultValue="How we found this place." />
        <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
          Five chapters, one patch of rocky Nova Scotia shoreline, and a retirement dream that kept getting bigger.
        </p>

        {/* Ornamental divider */}
        <div className="flex items-center justify-center gap-4 mt-10" aria-hidden="true">
          <span className="h-px w-16 bg-wc-teal/40" />
          <span className="font-accent italic text-wc-teal text-base">Whale Cove</span>
          <span className="h-px w-16 bg-wc-teal/40" />
        </div>
      </div>
    </motion.header>
  );
}

/* ─── Epilogue / closing CTA ─── */
function StoryEpilogue() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9 }}
      className="relative py-24 sm:py-32 px-6 bg-wc-forest overflow-hidden"
      data-testid="story-epilogue"
    >
      {/* Background image ghost */}
      <div className="absolute inset-0 opacity-20">
        <img src={cdn(IMG_FOG, 1200)} alt="" aria-hidden="true"
          loading="lazy" decoding="async"
          className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-wc-forest via-wc-forest/90 to-wc-forest" aria-hidden="true" />

      <div className="relative max-w-3xl mx-auto text-center">
        <p className="font-accent italic text-wc-teal text-base sm:text-lg mb-5">And now…</p>
        <h2 className="font-display text-wc-cream text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.15] mb-6">
          The next chapter is still being written.
        </h2>
        <p className="font-body text-wc-cream/75 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
          Site One opens for the 2027 season. Through 2026, we're on the land ourselves — hosting family and friends, testing every detail, walking permits through Digby Municipality. Site Two is a longer-term build (the road in still needs to happen). If you'd like to be part of the first group to put boots on this ground with a trailer in tow, we'd love to hear from you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/waitlist"
            data-testid="story-waitlist-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-wc-teal text-wc-forest font-body text-sm tracking-wide rounded-full hover:bg-wc-cream transition-colors">
            Join the waitlist <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/land"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-wc-teal/40 text-wc-cream font-body text-sm tracking-wide rounded-full hover:bg-wc-teal/10 transition-colors">
            See the land
          </Link>
        </div>

        {/* Quiet nod to George */}
        <div className="mt-16 pt-8 border-t border-wc-teal/15 max-w-md mx-auto">
          <p className="font-accent italic text-wc-cream/60 text-sm sm:text-base leading-relaxed">
            Thanks to George —
            <br className="sm:hidden" /> for the trail, and the conversation.
          </p>
        </div>
      </div>
    </motion.section>
  );
}

/* ─── Main ─── */
export default function Story() {
  return (
    <>
      <StoryIntro />
      <section id="story" data-testid="story-section" className="bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10">
          {CHAPTERS.map((chapter, i) => (
            <Chapter key={chapter.n} chapter={chapter} index={i} />
          ))}
        </div>
      </section>
      <StoryEpilogue />
    </>
  );
}
